import { useOptionalSidePanelPresentation } from 'designSystem/components/sidePanelContext'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { EvidenceChip } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/EvidenceChip'
import type { CitationFailureReason } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { citedQuotesFragment } from 'features/knowledgeDocumentViewer/utils/citedQuotesFragment'
import { useCallback, useMemo } from 'react'

import type { PreviewDocumentHandler } from './previewDocumentHandler'

interface ResponseSegmentCitationChipProps {
  fallbackDocumentId?: string
  failureReason?: CitationFailureReason | null
  onCitationHoverChange?: (highlighted: boolean) => void
  onPreviewDocument?: PreviewDocumentHandler
  pageNum: number
  quotes: string[]
  row: DocumentTableRow | undefined
}

export function ResponseSegmentCitationChip({
  fallbackDocumentId,
  failureReason,
  onCitationHoverChange,
  onPreviewDocument,
  pageNum,
  quotes,
  row,
}: ResponseSegmentCitationChipProps) {
  const { onOpenNewTab } = useSearchActions()
  const sidePanel = useOptionalSidePanelPresentation()
  const documentId = row?.id ?? fallbackDocumentId

  const citedQuotes = useMemo(
    () => (failureReason ? [] : quotes),
    [failureReason, quotes]
  )

  const handleOpenInNewTab = useCallback(() => {
    if (!documentId) return
    onOpenNewTab({
      documentId,
      pageNumber: pageNum,
      fragmentHighlight: citedQuotesFragment(pageNum, citedQuotes),
    })
  }, [citedQuotes, documentId, onOpenNewTab, pageNum])

  const handleClick = useCallback(() => {
    if (row && onPreviewDocument) {
      sidePanel?.keepOpenOnNextOutsideClick()
      onPreviewDocument(row, pageNum, citedQuotes)
      return
    }
    handleOpenInNewTab()
  }, [
    citedQuotes,
    handleOpenInNewTab,
    onPreviewDocument,
    pageNum,
    row,
    sidePanel,
  ])

  return (
    <EvidenceChip
      documentId={documentId}
      title={row ? row.documentTitle || row.title : undefined}
      drawing={row?.drawing ?? undefined}
      filename={row?.filename}
      bqDocumentId={row?.bqDocumentId ?? fallbackDocumentId}
      cantileverDocumentEntryId={row?.cantileverDocumentEntryId}
      documentHostsHydrated={Boolean(row)}
      pageNum={pageNum}
      quotes={quotes}
      failureReason={failureReason}
      onOpenChange={onCitationHoverChange}
      onClick={documentId ? handleClick : undefined}
      onOpenInNewTab={documentId ? handleOpenInNewTab : undefined}
    />
  )
}
